import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { removeEmpty } from 'src/app/@utils/remove-empty.helper';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    CustomerAccountVerificationStage,
    CustomerEmailStatus,
    CustomerViewModel,
    UpdateCustomerRequest
} from '@models/api-customers.models';
import {
    InvoiceSourceType,
    OrderHistoryOrder,
    OrderHistoryOrderInvoice,
    PointsViewModel
} from '@models/api-orders.models';
import { catchError, distinctUntilChanged, map, shareReplay, tap, withLatestFrom } from 'rxjs/operators';
import { checkCustomerFieldsData } from '@features/club/helper/check-customer-fields-data';
import { ClubNecessaryFields, CustomerVerificationStatus } from '@features/club/club.model';
import isEqual from 'lodash.isequal';
import { StorageService } from '@core/services/storage.service';

@Injectable({
    providedIn: 'root'
})
export class ClubService {
    private readonly userVerificationStatusKey = 'user-verification-stage';
    private readonly userVerificationStatus$ = new BehaviorSubject<CustomerVerificationStatus | null>(null);
    private readonly userVerificationStage$ = new ReplaySubject<CustomerAccountVerificationStage>(1);
    private readonly userPointsRS$ = new ReplaySubject<PointsViewModel>(1);
    private readonly userInfoBS$ = new BehaviorSubject<CustomerViewModel | null>(null);
    private userInfo$: Observable<CustomerViewModel>;
    private pointsInfo$: Observable<PointsViewModel>;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly storageService: StorageService
    ) {
        // Load the user verification status from the local storage in case it's available
        const userVerificationStatus = this.storageService.get<CustomerVerificationStatus>(
            this.userVerificationStatusKey
        );
        if (userVerificationStatus) {
            this.userVerificationStatus$.next(userVerificationStatus);
        }
    }

    public getClubPoints(): Observable<PointsViewModel> {
        if (!this.pointsInfo$) {
            this.pointsInfo$ = this.httpClient.get<PointsViewModel>(`orders-ms/loyalty/points`).pipe(
                tap(points => this.userPointsRS$.next(points)),
                shareReplay({
                    bufferSize: 1,
                    refCount: true
                })
            );
        }

        return this.pointsInfo$.pipe(
            withLatestFrom(this.userPointsRS$),
            distinctUntilChanged((a, b) => isEqual(a, b)),
            map(([oldPoints, newPoints]) => ({ ...oldPoints, ...newPoints }))
        );
    }

    public getUserInfo(): Observable<CustomerViewModel> {
        if (!this.userInfo$) {
            this.userInfo$ = this.httpClient.get<CustomerViewModel>(`customers-ms/user/info`).pipe(
                tap(user => {
                    this.saveVerificationStage(user);
                    this.userInfoBS$.next(user);
                }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true
                })
            );
        }

        return this.userInfo$.pipe(
            withLatestFrom(this.userInfoBS$),
            distinctUntilChanged((a, b) => isEqual(a, b)),
            map(([oldUser, newUser]) => {
                if (oldUser && newUser?.verificationStage !== oldUser.verificationStage) {
                    this.saveVerificationStage(newUser);
                }
                return { ...oldUser, ...newUser };
            })
        );
    }

    public getUserVerificationStage(): Observable<CustomerAccountVerificationStage | undefined> {
        return this.userVerificationStage$.asObservable();
    }

    /**
     * @description this will return the user verification status
     * @returns {Observable<CustomerVerificationStatus | null>}
     */
    public getUserVerificationStatus(): Observable<CustomerVerificationStatus | null> {
        return this.userVerificationStatus$.asObservable();
    }

    /**
     * @description this will clear the verification stage from the local storage
     *
     * For the case of the user it's logged in, and to make the user create and update in the correct way we
     * need to force the user to be logged out and when we are logged out we don't have the stage process anymore
     */
    public clearUserVerificationStatus(): void {
        this.storageService.remove(this.userVerificationStatusKey);
    }

    public getInvoices(): Observable<OrderHistoryOrderInvoice[]> {
        return this.httpClient.get<OrderHistoryOrderInvoice[]>(`orders-ms/loyalty/invoices/?startDate=1970-01-01`);
    }

    public getPendingOrders(): Observable<OrderHistoryOrder[]> {
        return this.httpClient.get<OrderHistoryOrder[]>(`orders-ms/loyalty/orders/?startDate=1970-01-01`);
    }

    public updateUserInfo(userInfo: CustomerViewModel): Observable<CustomerViewModel> {
        const updateRequest: UpdateCustomerRequest = this.mapUserInfoToUpdateRequest(userInfo);
        return this.httpClient.post<CustomerViewModel>(`customers-ms/user/update`, updateRequest).pipe(
            tap(() => {
                this.userInfoBS$.next(userInfo);
            })
        );
    }

    /**
     * @description this will update the user email
     * @param email
     *
     * previously this return a type like export type EmailChangeResultType = 'InvalidEmail' | 'EmailAlreadyUsed' | 'UpdateFailed' | 'UnableToUpdateNow' | 'NoNeedToChange' | 'EmailUpdated' | 'EmailNotFound';
     * but now only returns a string
     */
    public updateEmail(email: string): Observable<string> {
        return this.httpClient.post<string>(`customers-ms/user/email`, { email });
    }

    public downloadFile(invoiceId: string, sourceType: InvoiceSourceType): Observable<HttpResponse<Blob>> {
        return this.httpClient.get(`orders-ms/loyalty/invoices/${invoiceId}/pdf`, {
            responseType: 'blob',
            observe: 'response',
            params: {
                sourceType
            }
        });
    }

    public anonymizeUser<T>(): Observable<HttpResponse<T>> {
        return this.httpClient.delete<HttpResponse<T>>('customers-ms/user/anonymize');
    }

    public sendCustomerVerificationCodeEmail<T>(email: string): Observable<HttpResponse<T>> {
        return this.httpClient.post<HttpResponse<T>>(
            'customers-ms/AccountVerification/SendCustomerEmailVerificationCode',
            {
                email: email
            }
        );
    }

    public customerVerificationComplete<T>(verificationCode: string): Observable<T> {
        return this.httpClient
            .post<T>(
                'customers-ms/AccountVerification/CustomerVerificationComplete',
                {},
                {
                    params: {
                        verificationCode
                    }
                }
            )
            .pipe(tap(() => this.clearUserVerificationStatus()));
    }

    /**
     * this will check if the user has filled all the necessary fields only in case the user is logged in
     * @param fields
     * @returns {Observable<boolean>} return and observable of boolean
     */
    public hasCustomerDataFilled(fields?: ClubNecessaryFields | ClubNecessaryFields[]): Observable<boolean> {
        return this.userInfoBS$.pipe(map(userInfo => checkCustomerFieldsData(userInfo || {}, fields).length === 0));
    }

    /**
     * Deprecated
     * @param email
     */
    public validateMember(email: string): Observable<boolean> {
        if (!email) {
            return of(false);
        }

        return this.httpClient.get<boolean>(`customers-ms/Customer/CustomerExists`, { params: { email } }).pipe(
            catchError(() => of(false)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    /**
     * Will return 3 status depending on the customer status:
     * - Customer is confirmed | AccountConfirmed
     * - Customer is not confirmed | AccountNotConfirmed
     * - Customer doesn't exist nor in database or other status | EmailNotRegistered
     * @param email
     */
    public customerEmailStatus(email: string): Observable<CustomerEmailStatus> {
        if (!email) {
            return of('EmailNotRegistered');
        }

        return this.httpClient
            .get<CustomerEmailStatus>(`customers-ms/Customer/CustomerEmailStatus`, { params: { email } })
            .pipe(
                catchError(() => of('EmailNotRegistered') as Observable<CustomerEmailStatus>),
                shareReplay({ bufferSize: 1, refCount: true })
            );
    }

    private mapUserInfoToUpdateRequest(userInfo: CustomerViewModel): UpdateCustomerRequest {
        const {
            name: givenName,
            name2: surname,
            phoneNumber1,
            gender,
            birthDate: birthDateRaw,
            address: addressRaw,
            favoriteStoreId
        } = userInfo;

        const addressNoEmpty = removeEmpty(addressRaw || {});
        const address = Object.keys(addressNoEmpty).length === 0 ? undefined : { country: 'DK', ...addressNoEmpty }; // Sending undefined if there is nothing in the object

        const birthDate = birthDateRaw ? birthDateRaw.replaceAll('/', '-') : undefined; // Converting from DD/MM/YYYY to DD-MM-YYYY

        return {
            birthDate,
            givenName,
            surname,
            phoneNumber1,
            gender,
            address,
            favoriteStoreId
        };
    }

    /**
     * @description this will save the verification stage in the local storage as a sharedSession to
     * make sure when the user click on the link to activate the account, only happens once,
     * because the user could try start the process multiple times
     * @param user
     * @private
     */
    private saveVerificationStage(user?: CustomerViewModel | null): void {
        if (user) {
            this.storageService.set<CustomerVerificationStatus>(
                this.userVerificationStatusKey,
                {
                    email: user.email,
                    verificationStage: user.verificationStage
                },
                { sharedSession: true }
            );
        }

        this.userVerificationStage$.next(user?.verificationStage ?? 'Unspecified');
    }
}
